import BaseDir from '@/config/dir/BaseDir';
import crmClientApi from '@/api/crmClient';
import form from '@/utils/form';

/**
 * Справочник клиентов CRM.
 *
 * Клиенты живут в рамках компании: список фильтруется по ней через filter
 * (`{ company: id }` -> GET /api/crm_clients?company=id), а при создании
 * компания уходит в тело запроса уже как IRI.
 *
 * У клиента есть автор (owner) — тот, кто его завёл. Он же, наравне с
 * владельцем компании и заместителем, может клиента править, поэтому автора
 * показываем и даём переназначить. Кандидатов передаёт вызывающий в
 * context.owners: это владелец, заместители и участники воронок компании —
 * сервер не примет в авторы никого другого.
 */
export default class CrmClientDir extends BaseDir {
  get title() { return 'Клиенты'; }

  get itemTitle() { return 'Клиент'; }

  // eslint-disable-next-line class-methods-use-this
  get api() { return crmClientApi; }

  // eslint-disable-next-line class-methods-use-this
  getFields(context = {}, item = null) {
    const fields = {
      name: form.createFieldObject('text', 'Название', '', { required: 'Укажите название' }),
      phone: form.createFieldObject('tel', 'Телефон', ''),
      // формат почты бэк не проверяет — валидируем на фронте
      email: form.createFieldObject('text', 'Почта', '', { email: 'Некорректный адрес почты' }),
      // date, а не datetime: у дня рождения времени нет, и BaseDatepicker на
      // этом типе отдаёт полночь по UTC
      dateBirth: form.createFieldObject('date', 'День рождения', null),
      notes: form.createFieldObject('textarea', 'Заметки', ''),
    };

    // При создании автора не спрашиваем: им всегда становится создатель, и
    // переданное поле сервер всё равно проигнорирует
    if (item && context.owners?.length) {
      fields.owner = form.createUserFieldObject('Автор', context.owners, {
        emptyLabel: 'Нет доступных сотрудников',
      });
    }

    return fields;
  }

  /** Автор приходит объектом, а полю выбора пользователя нужен IRI. */
  getFormValues(item, context = {}) {
    return {
      ...super.getFormValues(item, context),
      owner: item?.owner?.['@id'] ?? item?.owner ?? null,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  getColumns() {
    return [
      {
        title: 'Название',
        width: 40,
        sortFn: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
        defaultSort: true,
        render: item => item.name,
      },
      {
        title: 'Телефон',
        width: 30,
        sortFn: (a, b) => (a.phone ?? '').localeCompare(b.phone ?? ''),
        render: item => item.phone || '—',
      },
      {
        title: 'Почта',
        width: 30,
        sortFn: (a, b) => (a.email ?? '').localeCompare(b.email ?? ''),
        render: item => item.email || '—',
      },
    ];
  }

  // eslint-disable-next-line class-methods-use-this
  getSearchIndex(item) {
    return [item.name, item.phone, item.email].filter(Boolean).join(' ');
  }

  // Компания обязательна при создании и после него не меняется, поэтому
  // подмешиваем её только здесь, а не в toRequestValues: иначе она полезла бы
  // и в PATCH, где поле не редактируется
  create(values, filter = {}) {
    const company = filter.company ? `/api/companies/${filter.company}` : null;
    return this.api.create(company ? { ...values, company } : values, true);
  }
}
